/**
 * controlModulations \u2014 vizz.fm-style LFO/audio routing applied to any numeric
 * `BioclastParams` field. Extends the engine without bloating the params
 * interface: modulators live in a separate map keyed by param name.
 *
 * Each modulator has:
 *   - `mode`: "audio" or "oscillate"
 *   - `freqStart`/`freqEnd`: which audio-band slice drives the value (audio mode);
 *     0..1 across the spectrum; bass=0..0.1, mid=0.1..0.4, treble=0.4..1.
 *   - `speed`: oscillator frequency in Hz (oscillate mode) OR audio response speed.
 *   - `easing`: shapes the 0..1 source signal: "linear" | "sine" | "ease-in" |
 *     "ease-out" | "ease-in-out".
 *   - `amount`: blend strength 0..1 (0 = ignored entirely).
 *   - `min`, `max`: output range; the eased signal is mapped into this range.
 *   - `invert`: flip the signal before mapping.
 *   - `enabled`: master toggle.
 *
 * Each frame the renderer calls `applyModulations(params, mods, features, t)`
 * which writes the modulated values into params in place. The original
 * (user-set) value is preserved by amount=0 falling through to it.
 */

import { AudioFeatures } from "../audio/types";
import { BioclastParams } from "./scenes/bioclast";

export type ModulationMode = "audio" | "oscillate";
export type ModulationEasing =
  | "linear"
  | "sine"
  | "ease-in"
  | "ease-out"
  | "ease-in-out";

export interface Modulation {
  enabled: boolean;
  mode: ModulationMode;
  amount: number;       // 0..1 \u2014 0 disables the effect even when enabled
  freqStart: number;    // 0..1 \u2014 audio mode only
  freqEnd: number;      // 0..1 \u2014 audio mode only
  invert: boolean;
  speed: number;        // Hz (oscillate) or smoothing (audio)
  easing: ModulationEasing;
  min: number;
  max: number;
}

export const DEFAULT_MODULATION: Modulation = {
  enabled: false,
  mode: "audio",
  amount: 1,
  freqStart: 0,
  freqEnd: 1,
  invert: false,
  speed: 1,
  easing: "sine",
  min: 0,
  max: 1,
};

/** Map of param name (string keyof BioclastParams) -> Modulation. */
export type ModulationMap = Partial<Record<keyof BioclastParams, Modulation>>;

function ease(t: number, kind: ModulationEasing): number {
  // Clamp first; some easings explode outside 0..1.
  const x = Math.max(0, Math.min(1, t));
  switch (kind) {
    case "linear": return x;
    case "sine": return 0.5 - 0.5 * Math.cos(Math.PI * x);
    case "ease-in": return x * x;
    case "ease-out": return 1 - (1 - x) * (1 - x);
    case "ease-in-out": return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
  }
}

/**
 * Read a "frequency slice" from features. Currently approximated from the 3
 * existing bands \u2014 cheap and good enough until we expose raw FFT bins.
 *
 * Mapping (matches BeatAnalyzer band ranges):
 *   0.00..0.10 -> bass
 *   0.10..0.40 -> mid
 *   0.40..1.00 -> treble
 *
 * The result is the average band energy across [freqStart, freqEnd].
 */
function audioSlice(f: AudioFeatures, lo: number, hi: number): number {
  const samples: Array<[number, number, number]> = [
    [0, 0.1, f.bass],
    [0.1, 0.4, f.mid],
    [0.4, 1.0, f.treble],
  ];
  let acc = 0;
  let weight = 0;
  for (const [a, b, val] of samples) {
    const overlap = Math.max(0, Math.min(b, hi) - Math.max(a, lo));
    if (overlap > 0) {
      acc += val * overlap;
      weight += overlap;
    }
  }
  return weight > 0 ? acc / weight : f.level;
}

/**
 * Apply every enabled modulation in `mods` to `params`. Original user values
 * for any non-modulated key are left untouched. Modulators that hit unknown
 * (non-numeric) keys are skipped silently.
 */
export function applyModulations(
  params: BioclastParams,
  mods: ModulationMap,
  features: AudioFeatures,
  timeSec: number,
): void {
  for (const key of Object.keys(mods) as (keyof BioclastParams)[]) {
    const m = mods[key];
    if (!m || !m.enabled || m.amount <= 0) continue;
    // Source signal in 0..1.
    let src: number;
    if (m.mode === "oscillate") {
      src = 0.5 + 0.5 * Math.sin(timeSec * m.speed * Math.PI * 2);
    } else {
      src = audioSlice(features, m.freqStart, m.freqEnd);
    }
    if (m.invert) src = 1 - src;
    const eased = ease(src, m.easing);
    const target = m.min + (m.max - m.min) * eased;
    // Amount blends between the existing value and the modulated target.
    const current = (params as unknown as Record<string, number>)[key as string];
    if (typeof current !== "number") continue;
    const next = current * (1 - m.amount) + target * m.amount;
    (params as unknown as Record<string, number>)[key as string] = next;
  }
}
